import { z } from 'zod';
import { CitationRef, Fact, SourceRef } from './ask.js';
import { RenderSpec } from './render-spec.js';
import { SynthesisSection } from './dossier.js';
import { ChatAction } from './chat-action.js';
import { Critique, RetrievalPlan, SubQuery } from './agent.js';
/**
 * Events emitted over the /api/ask SSE stream.
 *
 * The server always runs the planner first, so every stream begins
 * with `reasoning(parse) → reasoning(plan) → plan`. From there:
 *
 *   Direct path (planner returned `approach: 'direct'`):
 *     plan → entities → facts → sources → delta* → done
 *
 *   Agentic path (planner returned `approach: 'decomposed'`):
 *     plan → iteration → (subquery → tool_result)+ → facts → sources →
 *     critique → [iteration → … repeat] → delta* → done
 *
 * `reasoning` events interleave at each phase boundary on either path.
 * An `error` event may arrive at any point and terminates the stream.
 *
 * Old clients ignore unknown event types — `chat-pane` defaults the
 * switch to no-op so this is additive.
 *
 * `action` is **forward-compat** for server-side action emission. The
 * server does not emit it today; Stage-2 actions are encoded by the
 * model as `[ACTION]{...}[/ACTION]` blocks inside the streamed prose
 * and extracted by `parseAnswer` on the client once the stream
 * completes.
 */
export const AskSseEvent = z.discriminatedUnion('type', [
    z.object({ type: z.literal('reasoning'), step: z.string(), detail: z.string().optional() }),
    z.object({ type: z.literal('entities'), entities: z.array(z.string()) }),
    z.object({ type: z.literal('facts'), facts: z.array(Fact) }),
    z.object({ type: z.literal('sources'), sources: z.array(SourceRef) }),
    z.object({ type: z.literal('delta'), text: z.string() }),
    z.object({ type: z.literal('citation'), citation: CitationRef }),
    z.object({ type: z.literal('render'), spec: RenderSpec }),
    z.object({ type: z.literal('action'), action: ChatAction }),
    z.object({ type: z.literal('plan'), plan: RetrievalPlan, iteration: z.number().int().nonnegative() }),
    z.object({ type: z.literal('subquery'), subquery: SubQuery, iteration: z.number().int().nonnegative() }),
    z.object({
        type: z.literal('tool_result'),
        subqueryId: z.string(),
        tool: z.enum(['graph', 'vector', 'community']),
        factCount: z.number().int().nonnegative(),
        sourceCount: z.number().int().nonnegative(),
    }),
    z.object({ type: z.literal('critique'), critique: Critique, iteration: z.number().int().nonnegative() }),
    z.object({
        type: z.literal('iteration'),
        n: z.number().int().nonnegative(),
        max: z.number().int().positive(),
    }),
    z.object({ type: z.literal('done'), totalTokens: z.number().optional() }),
    z.object({ type: z.literal('error'), message: z.string() }),
]);
/**
 * Events on the /api/entity/:name/synthesis stream.
 * sections arrive one-at-a-time as Claude completes each object.
 */
export const SynthesisSseEvent = z.discriminatedUnion('type', [
    z.object({ type: z.literal('reasoning'), step: z.string(), detail: z.string().optional() }),
    z.object({ type: z.literal('section'), section: SynthesisSection }),
    z.object({ type: z.literal('done'), generatedAt: z.string(), model: z.string() }),
    z.object({ type: z.literal('error'), message: z.string() }),
]);
